/**
 * The Business Profile findings model (SGL-328), shared by the AI Visibility
 * dashboard's Opportunities card and the Visibility Manager's chat proposal so
 * both read, group and word findings identically.
 *
 * Pure: no React, no fetching. Reads the backend block defensively (every
 * field checked, unknown finding codes skipped, only http(s) links kept) and
 * groups the per-question findings into one gap per missing service.
 */

export const COMPETITORS_SHOWN = 3;
/** Same caps as the backend's per-finding evidence (profile_findings.py). */
const COMPETITORS_KEPT = 5;
const CITED_PAGES_KEPT = 5;

/* ─────────────────────────── defensive reading ─────────────────────────── */

/** A finding as rendered: every field present and of the right type. */
export interface CleanFinding {
  question: string;
  service: string;
  outcomes: [string, string][];
  competitors: string[];
  citedPages: { href: string; label: string }[];
  checked: { services: string[]; primary: string | null; additional: string[] } | null;
  expectedChange: string;
}

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

const textList = (v: unknown): string[] =>
  Array.isArray(v) ? v.map(text).filter(Boolean) : [];

/**
 * An http(s) URL with a real host, or null. The backend already filters to
 * http(s), but this is the line that stops a `javascript:` href reaching the
 * DOM, so it does not rely on that.
 */
export function safeCitedPage(raw: unknown): { href: string; label: string } | null {
  const s = text(raw);
  if (!/^https?:\/\//i.test(s)) return null;
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    return null;
  }
  if ((url.protocol !== "http:" && url.protocol !== "https:") || !url.hostname) return null;
  const host = url.hostname.replace(/^www\./, "");
  const path = url.pathname === "/" ? "" : url.pathname;
  return { href: url.href, label: `${host}${path}` };
}

export function cleanFinding(raw: unknown): CleanFinding | null {
  if (!isRecord(raw) || raw.code !== "service_not_listed") return null;
  const question = text(raw.question);
  const service = text(raw.service);
  if (!question || !service) return null;

  const outcomes: [string, string][] = isRecord(raw.outcomes)
    ? Object.entries(raw.outcomes)
        .filter(([engine]) => engine.trim() !== "")
        .map(([engine, role]) => [engine, typeof role === "string" ? role : "unknown"])
    : [];

  const seen = new Set<string>();
  const citedPages: CleanFinding["citedPages"] = [];
  for (const page of Array.isArray(raw.cited_pages) ? raw.cited_pages : []) {
    const safe = safeCitedPage(page);
    if (safe && !seen.has(safe.href)) {
      seen.add(safe.href);
      citedPages.push(safe);
    }
  }

  const pc = raw.profile_checked;
  const checked = isRecord(pc)
    ? {
        services: textList(pc.services),
        primary: text(pc.primary_category) || null,
        additional: textList(pc.additional_categories),
      }
    : null;

  return {
    question,
    service,
    outcomes,
    competitors: textList(raw.competitors),
    citedPages,
    checked,
    expectedChange: text(raw.expected_change),
  };
}

/* ─────────────────────────── grouping ──────────────────────────────────── */

/** One missing service and every lost question it affects. */
export interface FindingGroup {
  service: string;
  questions: { question: string; outcomes: [string, string][] }[];
  /** Merged across questions, most often named first, at most COMPETITORS_KEPT. */
  competitors: string[];
  citedPages: CleanFinding["citedPages"];
  checked: CleanFinding["checked"];
  expectedChange: string;
}

export const serviceKey = (s: string) => s.toLowerCase().replace(/\s+/g, " ");

/** Only for merging names across questions: the backend already merged
 * spellings within each question, so case and punctuation are all that is left. */
const nameKey = (s: string) => s.toLowerCase().replace(/[\s.,;:'’"&()/-]+/g, " ").trim();

/**
 * Findings grouped by service, in the order each service first appears (the
 * backend orders findings highest-intent first, so the first group is the gap
 * behind the most valuable question).
 */
export function groupFindings(findings: CleanFinding[]): FindingGroup[] {
  const groups = new Map<string, { group: FindingGroup; named: Map<string, { name: string; count: number }> }>();
  for (const f of findings) {
    const key = serviceKey(f.service);
    let entry = groups.get(key);
    if (!entry) {
      entry = {
        group: {
          service: f.service,
          questions: [],
          competitors: [],
          citedPages: [],
          checked: f.checked,
          expectedChange: f.expectedChange,
        },
        named: new Map(),
      };
      groups.set(key, entry);
    }
    const { group, named } = entry;
    group.questions.push({ question: f.question, outcomes: f.outcomes });
    group.checked = group.checked ?? f.checked;
    group.expectedChange = group.expectedChange || f.expectedChange;
    for (const name of f.competitors) {
      const k = nameKey(name);
      if (!k) continue;
      const seen = named.get(k);
      if (seen) seen.count += 1;
      else named.set(k, { name, count: 1 });
    }
    for (const page of f.citedPages) {
      if (group.citedPages.length < CITED_PAGES_KEPT && !group.citedPages.some((p) => p.href === page.href)) {
        group.citedPages.push(page);
      }
    }
  }
  return Array.from(groups.values(), ({ group, named }) => ({
    ...group,
    // Array.prototype.sort is stable, so equal counts keep first-seen order.
    competitors: Array.from(named.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, COMPETITORS_KEPT)
      .map((n) => n.name),
  }));
}

/* ─────────────────────────── wording ───────────────────────────────────── */

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** "A", "A and B", "A, B and C", "A, B, C and 2 more". */
export function namesList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length <= COMPETITORS_SHOWN) {
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, COMPETITORS_SHOWN).join(", ")} and ${names.length - COMPETITORS_SHOWN} more`;
}

export type Presence = "named" | "source" | "absent" | null;

/**
 * How the business showed up across engines, strongest first. "absent" only
 * when no engine named or cited them and at least one measurably left them
 * out; unknown engines prove nothing either way.
 */
export function presence(outcomes: [string, string][]): Presence {
  const roles = outcomes.map(([, role]) => role);
  // A lost question has no scorable "recommended" row, so this should not
  // happen; if it does, claim nothing about being named rather than guess.
  if (roles.includes("recommended")) return null;
  if (roles.includes("mentioned")) return "named";
  if (roles.includes("cited_source_only")) return "source";
  if (roles.includes("absent")) return "absent";
  return null;
}
